import User from '@/models/User'
import ForgotPassword from '@/models/User/ForgotPassword'
import ResetPassword from '@/models/User/ResetPassword'
import ChangePassword from '@/models/User/ChangePassword'
import { auth as backend } from '@/api'
import { services } from '@billow/js-helpers'
import { first, find } from 'lodash'


export default {

  namespaced: true,

  state: {
    user: new User,
    subscription: {},
    authenticated: false,
    forgotPassword: new ForgotPassword,
    resetPassword: new ResetPassword,
    changePassword: new ChangePassword,
    workflow: {
      created_at: '',
      user: {}
    },
    open_tasks: [],
  },

  mutations: {
    set: (state, payload) => state.user = payload,
    setSubscription: (state, payload) => state.subscription = payload,
    username: ({ user }, payload) => user.username = payload,
    password: ({ user }, payload) => user.password = payload,
    mobile: ({ user }, payload) => user.profile.mobile = payload,
    name: ({ user }, payload) => user.name = payload,
    surname: ({ user }, payload) => user.surname = payload,
    authenticated: (state) => state.authenticated = true,
    unauthenticated: (state) => state.authenticated = false,
    loggedIn: (state) => state.authenticated = true,

    // Forgot Password
    forgotPasswordEmail: (state, payload) => state.forgotPassword.email = payload,
    resetForgotPassword: state => state.forgotPassword = new ForgotPassword,

    // Reset Password
    resetPasswordEmail: (state, payload) => state.resetPassword.email = payload,
    resetPasswordPassword: (state, payload) => {
      state.resetPassword.password = payload,
      state.resetPassword.password_confirmation = payload
    },
    resetPasswordToken: (state, payload) => state.resetPassword.token = payload,
    resetResetPassword: state => state.resetPassword = new ResetPassword,

    // Change Password
    newPassword: (state, payload) => state.changePassword.password = payload,
    resetChangePassword: state => state.changePassword = new ChangePassword,
    setUserProfilePicture: (state, payload) => state.user.profile.picture_url = payload,

    clear: (state) => {
      state.subscription = {}
      state.user = new User
    },

    setWorkflow: (state, payload) => state.workflow = payload,
    setOpenTasks: (state, payload) => state.open_tasks = payload,
    isSetup: (state) => state.user.is_setup = true
  },

  actions: {
    login: ({ state, dispatch }) => new Promise((resolve, reject) => {
      backend.login(state.user, ({ data }) => {
        services.auth.setToken(
          data.access_token
        ).then(() => {
          dispatch('loadUser')
            .then(payload => resolve(payload.user))
            .catch(error => reject(error))
        })
      }, error => reject(error))
    }),
    logout: ({ commit, state }) => new Promise((resolve) => {
      let username = state.user.username
      services.auth.destroyToken()
      commit('unauthenticated')
      commit('clear')
      commit('username', username)
      resolve()
    }),
    loadUser: ({ commit }) => new Promise((resolve, reject) => {
      backend.loadUser(({ data }) => {
        commit('set', data.user)
        commit('setSubscription', data.subscription)
        commit('setWorkflow', data.workflow)
        commit('setOpenTasks', data.open_tasks)
        resolve(data)
      }, error => reject(error))
    }),
    checkAuth: ({ commit }) => {
      if (services.auth.token()) {
        commit('authenticated')
      } else {
        commit('unauthenticated')
      }
    },

    // Passwords
    requestPasswordReset: ({ state, commit }) => new Promise((resolve, reject) => {
      backend.requestPasswordReset(state.forgotPassword, response => {
        commit('resetForgotPassword')
        resolve()
      }, reject)
    }),
    resetPassword: ({ state, commit }, token) => new Promise((resolve, reject) => {
      commit('resetPasswordToken', token)
      backend.resetPassword(state.resetPassword, response => {
        commit('resetResetPassword')
        resolve()
      }, reject)
    }),
    changePassword: ({ state, commit }) => new Promise((resolve, reject) => {
      backend.changePassword(state.changePassword, response => {
        commit('password', '')
        resolve()
      }, reject)
    }),
    uploadProfilePicture: ({commit}, picture) => new Promise((resolve, reject) => {
      backend.uploadProfilePicture({
        picture
      }, ({data}) => {
        commit('setUserProfilePicture', data)
        resolve()
      }, error => reject(error))
    }),
    saveProfile: ({state}) => new Promise((resolve, reject) => {
      backend.saveProfile(state.user, () => {
        resolve()
      }, error => reject(error))
    }),
    update: ({state, commit}) => new Promise((resolve, reject) => {
      backend.update(state.user, () => {
        commit('isSetup')
        resolve()
      }, error => reject(error))
    })
  },

  getters: {
    user: state => state.user,
    authenticated: state => state.authenticated,
    abilities: state => state.user.abilities,
    role: (state, getters) => {
      if(!getters.subscription) return first(state.user.roles.filter(role => {
        return role.name !== 'user' && role.name !== 'super-user'
      }))
      return first(state.user.roles.filter(role => {
        return role.name !== 'super-user'
      }))
    },
    isCustomer: state => {
      return Boolean(
        find(state.user.roles, role => role.name === 'client-user')
      )
    },
    activeCompany: state => first(state.user.companies),
    companyAbilities: ({}, getters) => {
      return getters.activeCompany.abilities
    },
    hasSubscription: state => Boolean(state.subscription && state.subscription.active),
    subscriptionIsActive: state => {
      return Boolean(
        state.subscription 
        && state.subscription.active
        && !state.subscription.is_past
      )
    },
    isPayingPremium: (state, getters) => {
      return getters.subscriptionIsActive && (state.subscription.type.type === 'premium' || state.subscription.type.type === 'trial')
    },
    subscription: state => state.subscription,
    workflow: state => state.workflow,
    open_tasks: state => state.open_tasks,
    isAdmin: state => state.user.roles.filter(role => role.name === 'admin').length > 0
  }

}
